import { useEffect, useMemo, useState } from "react";
import { ExternalLink, Info } from "lucide-react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, fmtDate } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const LABEL = { rate: "Central bank policy rate", inflation: "Inflation" };
const AX = { tick: { fontSize: 11, fill: "#64748b" } };
const regionName = (() => {
  for (const loc of [undefined, "en"]) {
    try { const d = new Intl.DisplayNames(loc, { type: "region" }); if (d.of("AU") !== "AU") return (c) => d.of(c) || c; } catch { /* try next */ }
  }
  return (c) => c;
})();
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString(undefined, { month: "short", year: "numeric" });

const Tile = ({ label, pt, k, testid }) => (
  <div className="rounded-xl bg-[#F5F1EB] p-4" data-testid={testid}>
    <p className="text-xs text-slate-500">{label}</p>
    <p className="text-3xl font-semibold">{pt ? `${pt[k]}%` : "n/a"}</p>
    {pt && <p className="text-xs text-slate-500">{monthLabel(pt.month)}</p>}
  </div>
);

const Chart = ({ rows }) => (
  <ResponsiveContainer width="100%" height={300}>
    <LineChart data={rows} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
      <CartesianGrid stroke="#e2e8f0" vertical={false} />
      <XAxis dataKey="month" tickFormatter={(m) => m.slice(0, 4)} minTickGap={28} {...AX} /><YAxis unit="%" {...AX} />
      <Tooltip labelFormatter={monthLabel} formatter={(v, n) => [`${v}%`, LABEL[n]]} />
      <Legend formatter={(n) => LABEL[n]} wrapperStyle={{ fontSize: 12 }} />
      <Line dataKey="rate" type="stepAfter" stroke="#0f172a" strokeWidth={2.5} dot={false} isAnimationActive={false} connectNulls />
      <Line dataKey="inflation" stroke="#b45309" strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
    </LineChart>
  </ResponsiveContainer>
);

export const RatesInflation = () => {
  const [country, setCountry] = useState("");
  const [d, setD] = useState(null);
  useEffect(() => {
    let timer;
    const load = () => api.get("/stats/rates", { params: { country } }).then((r) => { if (r.data.loading) timer = setTimeout(load, 8000); setD(r.data); }).catch(() => setD(false));
    load();
    return () => clearTimeout(timer);
  }, [country]);
  const options = useMemo(() => (d?.countries || []).map((c) => ({ code: c, name: regionName(c) })).sort((a, b) => a.name.localeCompare(b.name)), [d?.countries]);
  if (d === false) return <p className="text-sm text-slate-500" data-testid="rates-error">Interest rate figures are unavailable right now.</p>;
  if (!d) return <section className="surface h-48 animate-pulse" data-testid="rates-loading" />;
  if (d.loading) return <p className="surface p-5 text-sm text-slate-500" data-testid="rates-preparing">Fetching the latest central bank figures. This takes a few seconds…</p>;
  const c = d.country;
  const name = c.name || (c.code ? regionName(c.code) : "");
  return (
    <section className="surface space-y-6 p-5 sm:p-6" data-testid="rates-inflation">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><p className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--facts))]">Official statistics</p>
          <h2 className="mt-1 text-2xl" data-testid="rates-title">Interest rates and inflation{name ? `: ${name}` : ""}</h2></div>
        <Select value={c.code || undefined} onValueChange={setCountry}>
          <SelectTrigger className="w-56" data-testid="rates-country-select"><SelectValue placeholder="Choose a country" /></SelectTrigger>
          <SelectContent>{options.map((o) => <SelectItem key={o.code} value={o.code}>{o.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {!c.covered ? <p className="text-sm text-slate-600" data-testid="rates-not-covered">{name || "Your country"}'s central bank rate isn't in the official international series. Choose another country above{!c.code && ", or set your location in Settings"}.</p> : <>
        <div className="grid gap-3 sm:grid-cols-2">
          <Tile label={`Policy rate set by ${d.bank.name}`} pt={d.latest.rate} k="rate" testid="rates-latest-rate" />
          <Tile label="Inflation (year on year)" pt={d.latest.inflation} k="inflation" testid="rates-latest-inflation" />
        </div>
        <div className="space-y-2" data-testid="rates-chart">
          <h3 className="text-lg">The last 25 years</h3>
          <p className="text-xs text-slate-500">Policy rate: monthly{c.euro ? " (the euro area shares one European Central Bank rate)" : ""}. Inflation: {d.inflation_note.toLowerCase()}.</p>
          {d.euro_note && <p className="text-xs text-slate-500" data-testid="rates-euro-note">{d.euro_note}</p>}
          <Chart rows={d.series} />
        </div>
        <aside className="flex gap-2 rounded-2xl border border-[hsl(var(--facts)/0.3)] bg-[hsl(var(--facts)/0.05)] p-4 text-sm text-slate-700" data-testid="rates-explainer">
          <Info size={16} className="mt-0.5 shrink-0 text-[hsl(var(--facts))]" />
          <p>The policy rate is the interest rate a central bank sets to steer borrowing costs. Banks base their loan and savings rates on it. Most central banks aim to keep inflation, the rise in consumer prices over a year, at around 2–3%.</p>
        </aside>
        <div className="flex flex-wrap gap-x-4">
          <a href={d.bank.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] font-medium text-[hsl(var(--facts))] hover:underline" data-testid="rates-bank-link">{d.bank.name}<ExternalLink size={10} /></a>
          {d.sources.map((s) => <a key={s.org} href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] font-medium text-[hsl(var(--facts))] hover:underline">{s.org}<ExternalLink size={10} /></a>)}
        </div>
      </>}
      <p className="text-[11px] text-slate-400" data-testid="rates-updated">From official central bank data, last checked {fmtDate(d.updated_at)}.</p>
    </section>
  );
};
